import React, { useState, useEffect } from 'react';
import {
  Grid,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  MenuItem,
  CircularProgress,
  Snackbar,
  Alert,
  useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SettingsIcon from '@mui/icons-material/Settings';
import { ParametroNotificacoes } from '../configuracoes/types';
import { notificationConfigService, NotificationSettings } from '../../services/notificationConfigService';
import { configSectionAccordionSx, configSectionAccordionSummarySx } from '../../styles/theme';

interface ConfiguracoesGeraisProps {
  parametros: ParametroNotificacoes;
  onChange: (field: keyof ParametroNotificacoes, value: any) => void;
}

const ConfiguracoesGerais: React.FC<ConfiguracoesGeraisProps> = ({
  parametros,
  onChange,
}) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });

  // Carregar configurações gerais do banco de dados
  useEffect(() => {
    loadGeneralSettings();
  }, []);

  const loadGeneralSettings = async () => {
    try {
      setLoading(true);
      const settings = await notificationConfigService.getNotificationSettings();
      
      // Atualizar os campos locais com os valores do banco
      onChange('horario_inicio_notificacoes', settings.horario_inicio_notificacoes);
      onChange('horario_fim_notificacoes', settings.horario_fim_notificacoes);
      onChange('nao_enviar_notificacoes_finais_semana', settings.nao_enviar_notificacoes_finais_semana);
      onChange('nao_enviar_notificacoes_feriados', settings.nao_enviar_notificacoes_feriados);
      onChange('envio_mensagens', settings.envio_mensagens);
      onChange('notificacoes_lembretes', settings.notificacoes_lembretes);
    } catch (error) {
      console.error('Erro ao carregar configurações gerais:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao carregar configurações gerais',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const saveGeneralSetting = async (key: keyof NotificationSettings, value: string | boolean) => {
    try {
      setSaving(true);
      await notificationConfigService.updateNotificationSetting(key, value);
      
      setSnackbar({
        open: true,
        message: 'Configuração salva com sucesso!',
        severity: 'success'
      });
    } catch (error) {
      console.error('Erro ao salvar configuração geral:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao salvar configuração',
        severity: 'error'
      });
      
      // Reverter o valor em caso de erro
      // Recarregar configurações para restaurar o valor original
      loadGeneralSettings();
    } finally {
      setSaving(false);
    }
  };

  const handleGeneralChange = async (field: keyof NotificationSettings, value: string | boolean) => {
    // Atualizar estado local imediatamente
    onChange(field as keyof ParametroNotificacoes, value);

    // Salvar no banco de dados apenas se não for campo de horário
    const isHorario = (field as string).includes('horario');
    if (!isHorario) {
      await saveGeneralSetting(field, value);
    }
  };

  const handleHorarioBlur = async (field: keyof NotificationSettings, value: string) => {
    // Salvar no banco de dados quando sair do campo de horário
    await saveGeneralSetting(field, value);
  };
  const templateOptions = [
    { value: 'padrao', label: 'Template Padrão' },
    { value: 'moderno', label: 'Template Moderno' },
    { value: 'minimalista', label: 'Template Minimalista' },
    { value: 'personalizado', label: 'Template Personalizado' },
  ];

  return (
    <Accordion disableGutters elevation={0} sx={configSectionAccordionSx(theme)}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={configSectionAccordionSummarySx(theme)}>
        <Box display="flex" alignItems="center">
          <SettingsIcon sx={{ mr: 1, color: 'info.main' }} />
          <Typography variant="h6">Configurações Gerais</Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Horário de início dos envios"
              type="time"
              value={parametros.horario_inicio_notificacoes}
              onChange={(e) => onChange('horario_inicio_notificacoes', e.target.value)}
              onBlur={(e) => handleHorarioBlur('horario_inicio_notificacoes', e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleHorarioBlur('horario_inicio_notificacoes', (e.target as HTMLInputElement).value);
                }
              }}
              InputLabelProps={{ shrink: true }}
              size="small"
              disabled={saving}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Horário de fim dos envios"
              type="time"
              value={parametros.horario_fim_notificacoes}
              onChange={(e) => onChange('horario_fim_notificacoes', e.target.value)}
              onBlur={(e) => handleHorarioBlur('horario_fim_notificacoes', e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleHorarioBlur('horario_fim_notificacoes', (e.target as HTMLInputElement).value);
                }
              }}
              InputLabelProps={{ shrink: true }}
              size="small"
              disabled={saving}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={parametros.nao_enviar_notificacoes_finais_semana}
                  onChange={(e) => handleGeneralChange('nao_enviar_notificacoes_finais_semana', e.target.checked)}
                  disabled={saving}
                />
              }
              label="Não enviar nos finais de semana"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={parametros.nao_enviar_notificacoes_feriados}
                  onChange={(e) => handleGeneralChange('nao_enviar_notificacoes_feriados', e.target.checked)}
                  disabled={saving}
                />
              }
              label="Não enviar em feriados"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={parametros.envio_mensagens}
                  onChange={(e) => handleGeneralChange('envio_mensagens', e.target.checked)}
                  disabled={saving}
                />
              }
              label="Envio de mensagens"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={parametros.notificacoes_lembretes}
                  onChange={(e) => handleGeneralChange('notificacoes_lembretes', e.target.checked)}
                  disabled={saving}
                />
              }
              label="Notificações de lembretes"
            />
          </Grid>
        </Grid>

        {/* Indicador de loading */}
        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" mt={2}>
            <CircularProgress size={24} />
            <Typography variant="body2" sx={{ ml: 1 }}>
              Carregando configurações gerais...
            </Typography>
          </Box>
        )}

        {/* Indicador de salvamento */}
        {saving && (
          <Box display="flex" justifyContent="center" alignItems="center" mt={2}>
            <CircularProgress size={20} />
            <Typography variant="body2" sx={{ ml: 1 }}>
              Salvando...
            </Typography>
          </Box>
        )}

        {/* Snackbar para notificações */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert 
            severity={snackbar.severity}
            onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </AccordionDetails>
    </Accordion>
  );
};

export default ConfiguracoesGerais; 